import { useEffect, useMemo, useState } from 'react';

import { Card, Statistic } from 'antd';
import type { FilterValue, TablePaginationConfig } from 'antd/es/table/interface';
import { useNavigate } from 'react-router';

import {
  type EApplicationStatus,
  useGetAdmissionApplicationsQuery,
  useGetAdmissionSurveyQuery,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { CustomTable, FetchDataWrapper } from '@/shared/components';
import { useLoader } from '@/shared/hooks';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';
import { ActionButtonsPanel } from '../_components/ActionButtonsPanel/ActionButtonsPanel';

import styles from './ApplicationsPage.module.scss';
import { formatApplicationsStats, getColumnsData } from './helpers';

interface IProp {
  schoolId: number;
}

export const ApplicationsPage = ({ schoolId }: IProp) => {
  const [applicationsTableFilters, setApplicationsTableFilters] = useState<Record<string, FilterValue | null>>({});
  const [applicationsTablePagination, setApplicationsTablePagination] = useState<TablePaginationConfig>({
    pageSize: 10,
    current: 1,
    total: 0,
  });

  const { hideLoader, showLoader } = useLoader();

  const {
    data: applicationsQueryData,
    isLoading: areApplicationsLoading,
    isFetching: areApplicationsFetching,
    isError: isApplicationsFetchError,
  } = useGetAdmissionApplicationsQuery({
    schoolId,
    page: applicationsTablePagination.current,
    perPage: applicationsTablePagination.pageSize,
    statusFilter: applicationsTableFilters.status as EApplicationStatus[],
  });
  const {
    data: surveyData,
    isLoading: isSurveyLoading,
    isFetching: isSurveyFetching,
    isError: isSurveyFetchError,
  } = useGetAdmissionSurveyQuery({
    schoolId,
  });

  const formattedApplicationsStats = useMemo(
    () => formatApplicationsStats(applicationsQueryData?.meta.stats),
    [applicationsQueryData?.meta.stats],
  );
  const columns = useMemo(() => getColumnsData({ filters: applicationsTableFilters }), [applicationsTableFilters]);

  useEffect(() => {
    if (applicationsQueryData?.meta.pagination.total_count) {
      setApplicationsTablePagination((prev) => ({
        ...prev,
        total: applicationsQueryData.meta.pagination.total_count,
      }));
    }
  }, [applicationsQueryData?.meta.pagination.total_count]);

  useEffect(() => {
    if (areApplicationsFetching || isSurveyFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [areApplicationsFetching || isSurveyFetching]);

  const navigate = useNavigate();

  return (
    <FetchDataWrapper
      isLoading={areApplicationsLoading || isSurveyLoading}
      isError={isApplicationsFetchError || isSurveyFetchError}
    >
      {applicationsQueryData && surveyData && (
        <div className={styles.wrapper}>
          <SelectionPanelHeader currentTab={ESelectionPanelTabs.Applications} />
          <div className={styles['page-info-wrapper']}>
            <p className={styles.title}>Этап 1: Заявки на опрос</p>
            <p className={styles.description}>Все заявки от кандидатов, прошедших опрос</p>
          </div>
          {formattedApplicationsStats && (
            <div className={styles['statistic-wrapper']}>
              {formattedApplicationsStats.map(({ id, title, value }) => (
                <Card
                  key={id}
                  className={styles['statistic-card']}
                  variant="borderless"
                >
                  <Statistic
                    title={title}
                    value={value}
                  />
                </Card>
              ))}
            </div>
          )}
          <ActionButtonsPanel
            schoolId={schoolId}
            applicationsStats={applicationsQueryData.meta.stats}
            surveyData={surveyData}
          />
          <CustomTable
            rowKey="id"
            onRow={({ id }) => ({
              onClick: () => {
                navigate(`${id}`);
              },
              style: {
                cursor: 'pointer',
              },
            })}
            pagination={{ ...applicationsTablePagination }}
            dataSource={applicationsQueryData.data}
            onChange={(pagination, filters, _, extra) => {
              setApplicationsTableFilters(filters);
              setApplicationsTablePagination({
                ...pagination,
                ...(extra.action === 'paginate' && pagination.pageSize !== applicationsTablePagination.pageSize
                  ? { current: 1 }
                  : {}),
              });
            }}
            columns={columns}
          />
        </div>
      )}
    </FetchDataWrapper>
  );
};
